import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container } from "../components/styled.ts";
import { Typography } from "../components/typography/index.tsx";
import { Header } from "../components/header/index.tsx";
import { Seo } from "../components/seo/index.tsx";
import { API_URLS } from "../utils/apiConfig.ts";
import { useMobile } from "../hooks/isMobile.tsx";

interface WrapSummary {
  wrapDate: string;
  slug: string;
  title: string;
  headline?: string;
  storyCount?: number;
  createdAt?: string;
}

const SLUG_PATTERN = /^finq-daily-wrap-\d{4}-\d{2}-\d{2}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const formatDate = (dateKey: string) => {
  if (!DATE_PATTERN.test(dateKey)) return dateKey;
  const [y, m, d] = dateKey.split("-").map((n) => parseInt(n, 10));
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const News = () => {
  const { isMobile } = useMobile();
  const [wraps, setWraps] = useState<WrapSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(API_URLS.WRAPS)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: WrapSummary[]) => {
        if (cancelled) return;
        const safe = Array.isArray(data)
          ? data.filter(
              (w) =>
                w &&
                typeof w.wrapDate === "string" &&
                DATE_PATTERN.test(w.wrapDate) &&
                typeof w.slug === "string" &&
                SLUG_PATTERN.test(w.slug) &&
                typeof w.title === "string"
            )
          : [];
        setWraps(safe);
      })
      .catch(() => {
        if (!cancelled) setWraps([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Seo
        title="FinQ Daily Wrap | Corporate India daily digest"
        description="End-of-day market pulse and the corporate filings that mattered — NSE & BSE results, orders, dividends and board meetings, summarised by AI."
        path="/news"
      />
      <Header active="news" />
      <div
        style={{
          background: "linear-gradient(180deg, #f1f5f9 0%, #ffffff 160px)",
          minHeight: "100vh",
        }}
      >
        <Container
          width={isMobile ? "min(calc(100% - 32px), 720px)" : "min(calc(100% - 48px), 720px)"}
          justifyContent="center"
          margin="0 auto"
          padding={isMobile ? "20px 0 64px" : "32px 0 80px"}
          column
          gap={"0"}
          style={{ boxSizing: "border-box" }}
        >
          <Typography
            tag="h1"
            fontFamily="Neue Montreal"
            fontSize={isMobile ? "26px" : "32px"}
            fontWeight={600}
            lineHeight={"1.2"}
            letterSpacing={"-0.02em"}
            color="#0f172a"
            textAlign="center"
            width="100%"
          >
            FinQ Daily Wrap
          </Typography>
          <Typography
            tag="p"
            fontFamily="Neue Montreal"
            fontSize={"16px"}
            fontWeight={400}
            lineHeight={"24px"}
            color="#64748b"
            margin={"8px 0 24px 0"}
            textAlign="center"
            width="100%"
          >
            End-of-day market pulse and the corporate filings that mattered — built for
            investors who scan, not scroll.
          </Typography>

          {loading && (
            <Typography tag="p" fontFamily="Neue Montreal" fontSize={"16px"} color="#64748b">
              Loading…
            </Typography>
          )}

          {!loading && wraps.length === 0 && (
            <Typography tag="p" fontFamily="Neue Montreal" fontSize={"16px"} color="#64748b">
              No wraps published yet. Check back after market hours.
            </Typography>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {wraps.map((wrap) => (
              <Link
                key={wrap.wrapDate}
                to={`/news/${encodeURIComponent(wrap.slug)}`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: isMobile ? "16px" : "18px 20px",
                  cursor: "pointer",
                  boxSizing: "border-box",
                  fontFamily: "Neue Montreal, system-ui, sans-serif",
                  textDecoration: "none",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    color: "#2563eb",
                    marginBottom: 6,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatDate(wrap.wrapDate)}
                </div>
                <div
                  style={{
                    fontSize: isMobile ? 17 : 18,
                    fontWeight: 600,
                    color: "#0f172a",
                    lineHeight: 1.35,
                    letterSpacing: "-0.01em",
                    marginBottom: wrap.headline ? 6 : 0,
                  }}
                >
                  {wrap.title}
                </div>
                {wrap.headline && (
                  <div
                    style={{
                      fontSize: 14,
                      lineHeight: "21px",
                      color: "#64748b",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {wrap.headline.replace(/\*\*/g, "")}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </Container>
      </div>
    </>
  );
};

export default News;
